import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Prompt } from '@app-libs/model';
import { ConfigurationClientService } from '../client/configuration-client.service';
import { ApiClient } from '../client/api-client';

@Injectable({
  providedIn: 'root',
})
export class PromptsApiService extends ApiClient {
  constructor(config: ConfigurationClientService, http: HttpClient) {
    super(config, http);
  }

  override get baseUrl(): string {
    return this.config.getBackendAPIUrl() + '/prompts';
  }

  /**
   * Create a new prompt.
   */
  createPrompt(prompt: Partial<Prompt>): Observable<Prompt | null> {
    return this.http.post<Prompt | null>(`${this.baseUrl}`, prompt);
  }

  /**
   * Update an existing prompt.
   */
  updatePrompt(
    promptId: string,
    updates: Partial<Prompt>
  ): Observable<Prompt | null> {
    return this.http.patch<Prompt | null>(
      `${this.baseUrl}/${promptId}`,
      updates
    );
  }

  /**
   * Delete a prompt by ID.
   */
  deletePrompt(promptId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/${promptId}`);
  }

  /**
   * Fetch all prompts with optional filters.
   */
  fetchAllPrompts(limit = 20, offset = 0): Observable<Prompt[]> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.http.get<Prompt[]>(`${this.baseUrl}`, { params });
  }

  /**
   * Fetch prompt versions by prompt ID.
   */
  fetchPromptVersions(promptId: string): Observable<never[]> {
    return this.http.get<never[]>(`${this.baseUrl}/${promptId}/versions`);
  }

  /**
   * Set active status of a prompt.
   */
  setPromptActiveStatus(
    promptId: string,
    isActive: boolean
  ): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/${promptId}/active`, {
      isActive,
    });
  }

  /**
   * Increment the usage count of a prompt.
   */
  incrementUsageCount(promptId: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}/${promptId}/increment-usage`,
      {}
    );
  }

  /**
   * Fetch prompts by permission type.
   */
  fetchPromptsByPermission(
    permissionType: 'global' | 'user' | 'workspace'
  ): Observable<Prompt[]> {
    const params = new HttpParams().set('type', permissionType);
    return this.http.get<Prompt[]>(`${this.baseUrl}/permissions`, { params });
  }

  /**
   * Clone a prompt by ID.
   */
  clonePrompt(promptId: string): Observable<Prompt | null> {
    return this.http.post<Prompt | null>(
      `${this.baseUrl}/${promptId}/clone`,
      {}
    );
  }

  /**
   * Fetch prompts by tags.
   */
  fetchPromptsByMultipleTags(tags: string[]): Observable<Prompt[]> {
    const params = new HttpParams().set('tags', tags.join(','));
    return this.http.get<Prompt[]>(`${this.baseUrl}/tags`, { params });
  }

  /**
   * Fetch prompts by category and workspace.
   */
  fetchPromptsByCategoryAndWorkspace(
    category: string,
    workspaceId: string
  ): Observable<Prompt[]> {
    const params = new HttpParams()
      .set('category', category)
      .set('workspaceId', workspaceId);
    return this.http.get<Prompt[]>(`${this.baseUrl}/category`, { params });
  }

  /**
   * Fetch prompts by user role.
   */
  fetchPromptsByUserRole(role: string): Observable<Prompt[]> {
    const params = new HttpParams().set('role', role);
    return this.http.get<Prompt[]>(`${this.baseUrl}/roles`, { params });
  }

  /**
   * Duplicate a prompt with a new name.
   */
  duplicatePromptWithName(
    promptId: string,
    newName: string
  ): Observable<Prompt | null> {
    return this.http.post<Prompt | null>(
      `${this.baseUrl}/${promptId}/duplicate`,
      { newName }
    );
  }

  /**
   * Fetch prompts created by a specific user.
   */
  fetchPromptsByUser(userId: string): Observable<Prompt[]> {
    return this.http.get<Prompt[]>(`${this.baseUrl}/users/${userId}`);
  }

  /**
   * Fetch prompts sorted alphabetically by name.
   */
  fetchPromptsAlphabetically(
    order: 'asc' | 'desc' = 'asc'
  ): Observable<Prompt[]> {
    const params = new HttpParams().set('order', order);
    return this.http.get<Prompt[]>(`${this.baseUrl}/alphabetical`, { params });
  }
}
